import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import newsletterUnsubscribeService from '../../services/newsletterUnsubscribeService';
import {
	Box,
	Typography,
	TextField,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Company from '../../components/company/company';
import styles from './NewsletterUnsubscribes.module.css';

const NewsletterUnsubscribes = () => {
	const navigate = useNavigate();
	const [newsletterId, setNewsletterId] = useState('');
	const [unsubscribes, setUnsubscribes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [successMessage, setSuccessMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [hasSearched, setHasSearched] = useState(false);

	// Fetch unsubscribes based on newsletterId
	const fetchUnsubscribes = async () => {
		setLoading(true);
		setSuccessMessage('');
		setErrorMessage('');
		setHasSearched(true);
		try {
			const response =
				await newsletterUnsubscribeService.getNewsletterUnsubscribes(newsletterId);
			setUnsubscribes(response.data);
			setSuccessMessage('Successfully fetched unsubscribes.');
		} catch (error) {
			setErrorMessage('Error fetching unsubscribes. Please try again.');
			console.error('Error fetching unsubscribes:', error);
		}
		setLoading(false);
	};

	// Handle form submit
	const handleSubmit = (e) => {
		e.preventDefault();
		fetchUnsubscribes();
	};

	// Delete an unsubscribe entry
	const handleDelete = async (unsubscribeId, companyId) => {
		console.log({ message: 'here' });
		setSuccessMessage('');
		setErrorMessage('');
		try {
			const response =
				await newsletterUnsubscribeService.deleteNewsletterUnsubscribes(
					unsubscribeId,
					companyId
				);
			console.log({ response: response.data });
			setUnsubscribes(response.data);
			setUnsubscribes(
				unsubscribes.filter((item) => item.unsubscribeId !== unsubscribeId)
			);
			setSuccessMessage('Successfully deleted unsubscribe.');
		} catch (error) {
			setErrorMessage('Error deleting unsubscribe. Please try again.');
			console.error('Error deleting unsubscribe:', error);
		}
	};

	return (
		<>
			<Company />
			<div className={styles.container}>
				<div className={styles.headerContainer}>
					<div className={styles.headerRow}>
						<Button
							className={styles.backButton}
							onClick={() => navigate('/home')}
							aria-label='back to home'
							startIcon={<ArrowBackIcon />}
						>
							Back
						</Button>
						<Typography className={styles.pageTitle}>
							Newsletter Unsubscribe Manager
						</Typography>
					</div>
					<Typography className={styles.pageSubtitle}>
						Manage and track newsletter unsubscribe requests
					</Typography>
				</div>

				<Paper className={styles.contentPaper}>
					<Box
						component='form'
						onSubmit={handleSubmit}
						className={styles.formSection}
						sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
					>
						<TextField
							label='Newsletter ID'
							variant='outlined'
							value={newsletterId}
							onChange={(e) => setNewsletterId(e.target.value)}
							required
							fullWidth
							sx={{
								'& .MuiOutlinedInput-root': {
									'&:hover fieldset': {
										borderColor: '#28BCBB',
									},
									'&.Mui-focused fieldset': {
										borderColor: '#28BCBB',
									},
								},
								'& .MuiInputLabel-root.Mui-focused': {
									color: '#28BCBB',
								},
							}}
						/>
						<Button 
							variant='contained' 
							type='submit'
							className={styles.searchButton}
						>
							Fetch Unsubscribes
						</Button>
					</Box>

					{loading ? (
						<Box className={styles.loadingContainer}>
							<CircularProgress sx={{ color: '#28BCBB' }} />
						</Box>
					) : unsubscribes.length > 0 ? (
						<TableContainer component={Paper} className={styles.tableContainer}>
							<Table>
								<TableHead className={styles.tableHead}>
									<TableRow>
										<TableCell className={styles.tableHeadCell}>Newsletter ID</TableCell>
										<TableCell className={styles.tableHeadCell}>Email</TableCell>
										<TableCell className={styles.tableHeadCell}>Unsubscribe ID</TableCell>
										<TableCell className={styles.tableHeadCell}>Reason</TableCell>
										<TableCell className={styles.tableHeadCell} align='center'>Actions</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{unsubscribes.map((unsubscribe) => (
										<TableRow key={unsubscribe.unsubscribeId} className={styles.tableRow}>
											<TableCell className={styles.tableCell}>{unsubscribe.newsletterId}</TableCell>
											<TableCell className={styles.tableCell}>{unsubscribe.email}</TableCell>
											<TableCell className={styles.tableCell}>{unsubscribe.unsubscribeId}</TableCell>
											<TableCell className={styles.tableCell}>
												{unsubscribe.reason || 'No reason provided'}
											</TableCell>
											<TableCell className={styles.tableCell} align='center'>
												<Button
													variant='contained'
													className={styles.deleteButton}
													onClick={() =>
														handleDelete(unsubscribe.unsubscribeId, unsubscribe.companyId)
													}
													size='small'
												>
													Delete
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : hasSearched && unsubscribes.length === 0 && !loading ? (
						<Box className={styles.emptyState}>
							<Typography>No unsubscribes found for this newsletter ID.</Typography>
						</Box>
					) : null}

					{/* Success or Error Message */}
					{successMessage && (
						<Alert severity='success' className={styles.alertContainer}>
							{successMessage}
						</Alert>
					)}
					{errorMessage && (
						<Alert severity='error' className={styles.alertContainer}>
							{errorMessage}
						</Alert>
					)}
				</Paper>
			</div>
		</>
	);
};

export default NewsletterUnsubscribes;
